import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { CornerDownRight, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePermission } from '@/features/permissions/usePermission'
import { Input } from '@/components/ui/input'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { useCrm2Accounts, type AccountRow } from './api'
import { NewAccountDialog } from './NewAccountDialog'
import { Crm2Frame, LINK_CHIP, money, shortDay, usePeopleNames } from './shared'

/** Every company in TapCRMv2: where it is in the flow, its people, and its open value. */
export function AccountsPage() {
  const { workspaceSlug = '' } = useParams()
  const [q, setQ] = useState('')
  const [kind, setKind] = useState<'all' | 'customers' | 'prospects'>('all')
  const list = useCrm2Accounts(q.trim())
  const name = usePeopleNames()
  const navigate = useNavigate()
  const { can } = usePermission()
  const [adding, setAdding] = useState(false)
  const rows = treeOrder((list.data ?? []).filter((a) => kind === 'all' || (kind === 'customers' ? a.customer : !a.customer)))
  return (
    <Crm2Frame
      title="Accounts"
      description="One account per company. A big enterprise can hold sub-accounts per region or department, each with its own deals and people."
      actions={
        <Button size="sm" onClick={() => setAdding(true)} disabled={!can('create', 'Crm')} title={can('create', 'Crm') ? undefined : 'You need permission to add to the CRM'}>
          <Plus className="size-4" />
          New account
        </Button>
      }
    >
      {adding ? <NewAccountDialog onClose={() => setAdding(false)} onCreated={(id) => navigate(paths.crm2Account(workspaceSlug, id))} /> : null}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or domain" className="h-9 w-64 pl-8" aria-label="Find an account" />
        </div>
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Which accounts">
          {(['all', 'customers', 'prospects'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)} className={cn('rounded-md px-3 py-1 capitalize', kind === k ? 'bg-accent font-medium' : 'text-muted-foreground')}>
              {k}
            </button>
          ))}
        </div>
      </div>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <LoadingState label="Loading accounts" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-border-soft text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Where</th>
                <th className="px-4 py-2 font-medium">Owner</th>
                <th className="px-4 py-2 text-right font-medium">Open in Sales</th>
                <th className="px-4 py-2 text-right font-medium">People</th>
                <th className="px-4 py-2 font-medium">Renews</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-soft">
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-accent/40">
                  <td className="px-4 py-2" style={{ paddingLeft: `${1 + a.depth * 1.5}rem` }}>
                    {a.depth ? <CornerDownRight className="mr-1.5 inline size-3.5 text-text-muted" /> : null}
                    <Link to={paths.crm2Account(workspaceSlug, a.id)} className={cn(LINK_CHIP, 'text-sm', a.depth ? 'font-normal' : 'font-semibold')}>{a.name}</Link>
                    {a.subs ? <span className="ml-2 rounded-full bg-muted px-1.5 text-tiny text-muted-foreground">{a.subs} sub-account{a.subs === 1 ? '' : 's'}</span> : null}
                    {a.domain ? <span className="ml-2 text-xs text-muted-foreground">{a.domain}</span> : null}
                    {a.customer ? <span className="ml-2 rounded-full bg-success-soft px-1.5 text-tiny font-medium text-success">Customer</span> : null}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{a.where?.label ?? 'No open deal'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{name(a.ownerId)}</td>
                  <td className="num px-4 py-2 text-right">{a.openValue ? money(a.openValue) : '—'}</td>
                  <td className="num px-4 py-2 text-right">{a.contacts}</td>
                  <td className="px-4 py-2 text-muted-foreground">{shortDay(a.renewalAt)}</td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No accounts yet. New deals create them; Settings can add sample companies.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </Crm2Frame>
  )
}

/**
 * Main accounts in name order, each followed by its sub-accounts (and theirs). A sub-account whose
 * main account is filtered out stands at the top, so a search never hides a match.
 */
function treeOrder(list: AccountRow[]): Array<AccountRow & { depth: number; subs: number }> {
  const ids = new Set(list.map((a) => a.id))
  const kids = new Map<string, AccountRow[]>()
  const top: AccountRow[] = []
  for (const a of list) {
    if (a.parentId && ids.has(a.parentId)) kids.set(a.parentId, [...(kids.get(a.parentId) ?? []), a])
    else top.push(a)
  }
  const byName = (x: AccountRow, y: AccountRow) => x.name.localeCompare(y.name)
  const out: Array<AccountRow & { depth: number; subs: number }> = []
  const walk = (a: AccountRow, depth: number) => {
    const children = (kids.get(a.id) ?? []).sort(byName)
    out.push({ ...a, depth, subs: children.length })
    if (depth < 5) for (const c of children) walk(c, depth + 1)
  }
  for (const a of top.sort(byName)) walk(a, 0)
  return out
}
